import React, { useMemo, useState } from "react";
import { Button, Form, FormControl, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import "./ActiveUnitSelector.css";

const parseJSON = (value, fallback) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const getUnitsFromStorage = () => {
  const storedUnits = parseJSON(localStorage.getItem("units"), []);
  const storedCooperative = parseJSON(localStorage.getItem("cooperative"), null);

  const normalizedUnits = Array.isArray(storedUnits) ? storedUnits : [];
  const uniqueUnits = normalizedUnits.reduce((acc, unit) => {
    if (!unit?._id) return acc;
    if (!acc.some((item) => item?._id === unit._id)) {
      acc.push(unit);
    }
    return acc;
  }, []);

  if (storedCooperative?._id && !uniqueUnits.some((item) => item?._id === storedCooperative._id)) {
    uniqueUnits.unshift(storedCooperative);
  }

  return uniqueUnits;
};

const ActiveUnitSelector = ({ dark = false, compact = false, onUnitChanged }) => {
  const user = parseJSON(localStorage.getItem("user"), {});
  const hasAllowedUnitIds =
    Array.isArray(user?.allowedUnitIds) && user.allowedUnitIds.length > 0;
  const units = useMemo(() => getUnitsFromStorage(), []);
  const fallbackUnitId = units[0]?._id || "";
  const [selectedId, setSelectedId] = useState(
    localStorage.getItem("activeUnitId") || parseJSON(localStorage.getItem("cooperative"), {})?._id || fallbackUnitId
  );
  const [showModal, setShowModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  if (!hasAllowedUnitIds || !units.length || units.length === 1) {
    return null;
  }

  const selectedUnit = units.find((unit) => unit?._id === selectedId) || units[0];
  const filteredUnits = units.filter((unit) =>
    (unit?.name || "").toLowerCase().includes(searchValue.toLowerCase())
  );
  const tooltipText = selectedUnit?.name
    ? `Unidade ativa: ${selectedUnit.name}. Clique para alterar.`
    : "Selecionar unidade ativa";
  const shouldShowTooltip = compact;

  const handleSelect = (nextUnitId) => {
    if (!nextUnitId || nextUnitId === selectedId) {
      return;
    }

    const nextUnit = units.find((unit) => unit?._id === nextUnitId);
    if (!nextUnit) {
      return;
    }

    setSelectedId(nextUnitId);
    localStorage.setItem("cooperative", JSON.stringify(nextUnit));
    localStorage.setItem("activeUnitId", nextUnitId);
    window.dispatchEvent(new CustomEvent("sgu-active-unit-changed", { detail: { unitId: nextUnitId } }));

    if (typeof onUnitChanged === "function") {
      onUnitChanged(nextUnit);
    }

    setShowModal(false);
    setSearchValue("");
    window.location.href = "/home";
  };

  const triggerButton = (
    <Button
      type="button"
      onClick={() => setShowModal(true)}
      title={compact ? "Selecionar unidade ativa" : undefined}
      className={`active-unit-trigger w-100 d-flex align-items-center ${compact ? "justify-content-center active-unit-trigger-compact" : "justify-content-between"} ${dark ? "active-unit-trigger-dark" : "active-unit-trigger-light"}`}
      variant="link"
    >
      <span className="d-flex align-items-center text-start" style={{ gap: compact ? 0 : 10, minWidth: 0 }}>
        <HiOutlineOfficeBuilding size={18} />
        {!compact && (
          <span className="text-truncate" style={{ minWidth: 0 }}>
            {selectedUnit?.name || "Selecionar unidade"}
          </span>
        )}
      </span>
      {!compact && <span className="active-unit-trigger-action">ALTERAR</span>}
    </Button>
  );

  return (
    <Form.Group className="mb-2">
      {shouldShowTooltip ? (
        <OverlayTrigger
          placement="right"
          overlay={<Tooltip id="active-unit-tooltip" className="active-unit-tooltip">{tooltipText}</Tooltip>}
        >
          <span className="d-block">{triggerButton}</span>
        </OverlayTrigger>
      ) : (
        triggerButton
      )}

      <Modal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSearchValue("");
        }}
        centered
        dialogClassName="active-unit-modal-dialog"
      >
        <Modal.Header closeButton className="active-unit-modal-header">
          <Modal.Title>Selecionar unidade</Modal.Title>
        </Modal.Header>
        <Modal.Body className="active-unit-modal-body">
          <FormControl
            size="sm"
            type="text"
            placeholder="Filtrar unidade..."
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            className="mb-3 active-unit-search-input"
          />

          <div className="active-unit-list-wrapper">
            {filteredUnits.length ? (
              filteredUnits.map((unit) => {
                const isActive = unit._id === selectedId;
                return (
                  <Button
                    key={unit._id}
                    type="button"
                    className={`w-100 mb-2 text-start d-flex justify-content-between align-items-center active-unit-item ${isActive ? "active-unit-item-selected" : ""}`}
                    onClick={() => handleSelect(unit._id)}
                    variant="link"
                  >
                    <span className="text-truncate">{unit?.name || "Unidade sem nome"}</span>
                    {isActive ? <span className="active-unit-item-badge">ATUAL</span> : null}
                  </Button>
                );
              })
            ) : (
              <div className="px-2 py-1 active-unit-empty">
                Nenhuma unidade encontrada.
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="active-unit-modal-footer">
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false);
              setSearchValue("");
            }}
          >
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </Form.Group>
  );
};

export default ActiveUnitSelector;
